import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, inject, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FirebaseAuthService } from './core/auth/firebase-auth.service';
import { provideFirebaseApp, initializeApp, FirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth as initializeFirebaseAuth
} from 'firebase/auth';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { AppEnvironment, APP_ENV } from './core/app-environment';
import { routes } from './app.routes';
import { authInterceptor } from './core/auth/auth.interceptor';
import { familyInterceptor } from './core/families/family.interceptor';
import { APIS, Configuration } from './api/generated';
import { MatSnackBarModule } from '@angular/material/snack-bar';

const messagingSupported = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const hasNotification = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  return hasNotification && hasServiceWorker && hasPushManager;
};

export const buildAppConfig = (env: AppEnvironment): ApplicationConfig => {
  const providers: ApplicationConfig['providers'] = [
    { provide: APP_ENV, useValue: env },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, familyInterceptor])),
    provideAnimationsAsync(),
    provideFirebaseApp(() => initializeApp(env.firebase)),
    provideAuth(() => {
      const app = inject(FirebaseApp);
      if (typeof window === 'undefined') {
        console.info('[FirebaseAuthInit] SSR environment detected; returning existing auth instance');
        return getAuth(app);
      }

      const userAgent = navigator.userAgent ?? '';
      const isIOS = /iP(ad|hone|od)/i.test(userAgent);
      const isTouchMac = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
      const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(userAgent);
      const isStandalone = typeof navigator !== 'undefined' && 'standalone' in navigator && Boolean((navigator as unknown as { standalone?: boolean }).standalone);
      const shouldForceSessionPersistence = (isIOS || isTouchMac) && isSafari;

      console.info('[FirebaseAuthInit] Environment detection', {
        userAgent,
        isIOS,
        isTouchMac,
        isSafari,
        isStandalone,
        shouldForceSessionPersistence
      });

      const persistenceFallbacks = shouldForceSessionPersistence
        ? [browserSessionPersistence]
        : [
          indexedDBLocalPersistence,
          browserLocalPersistence,
          browserSessionPersistence
        ];

      console.info('[FirebaseAuthInit] Attempting to initialize auth with persistence fallbacks', persistenceFallbacks.map((p) => p.type));

      try {
        const instance = initializeFirebaseAuth(app, {
          persistence: persistenceFallbacks,
          popupRedirectResolver: browserPopupRedirectResolver
        });
        console.info('[FirebaseAuthInit] Successfully initialized auth with custom persistence configuration');
        return instance;
      }
      catch (error) {
        console.warn('[FirebaseAuthInit] Failed to initialize auth with custom persistence; falling back to existing instance', error);
        // Auth already initialized, return existing instance
        return getAuth(app);
      }
    }),
    { provide: Configuration, useFactory: () => new Configuration({ basePath: env.apiBaseUrl }) },
    ...APIS,
    importProvidersFrom(MatSnackBarModule),
    provideServiceWorker('ngsw-worker.js', {
      enabled: env.production,
      registrationStrategy: 'registerImmediately'
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (auth: FirebaseAuthService) => () => auth.waitForInitialization(),
      deps: [FirebaseAuthService],
      multi: true
    }
  ];

  if (messagingSupported()) {
    console.info('[FirebaseMessaging] Browser supports messaging APIs; enabling Firebase Messaging');
    providers.push(provideMessaging(() => getMessaging()));
  } else {
    console.info('[FirebaseMessaging] Browser does not support required messaging APIs; skipping Firebase Messaging initialization');
  }

  return { providers };
};
